import { catchError, finalize, tap } from 'rxjs/operators'
import { inject } from '@angular/core'
import { Actions } from '@ngrx/effects'
import { Action, MemoizedSelector, Store } from '@ngrx/store'
import { MonoTypeOperatorFunction, Observable, OperatorFunction, throwError } from 'rxjs'
import { concatLatestFrom } from '@ngrx/operators'

export abstract class EffectsBase {
  protected readonly _actions$ = inject(Actions)
  protected readonly _store = inject(Store)

  protected dispatch<T>(action: Action): MonoTypeOperatorFunction<T> {
    return tap(() => this._store.dispatch(action))
  }

  protected dispatchOnFinalize<T>(action: Action): MonoTypeOperatorFunction<T> {
    return finalize(() => this._store.dispatch(action))
  }

  protected dispatchOnError<T, R = never>(
    failureAction: Action,
    returnObs?: Observable<R>,
  ): OperatorFunction<T, T | R> {
    return catchError((error: any) => {
      this._store.dispatch(failureAction)
      console.error(error)
      return returnObs ?? throwError(() => error)
    })
  }

  protected withLatestFrom<T, S1>(
    selector1: MemoizedSelector<object, S1>,
  ): OperatorFunction<T, [T, S1]>

  protected withLatestFrom<T, S extends any[]>(
    selectors: [...{ [K in keyof S]: MemoizedSelector<object, S[K]> }],
  ): OperatorFunction<T, [T, ...S]>

  protected withLatestFrom<T>(
    selectorOrSelectors: MemoizedSelector<object, any> | readonly MemoizedSelector<object, any>[],
  ): OperatorFunction<T, [T, any] | [T, ...any[]]> {
    if (Array.isArray(selectorOrSelectors)) {
      return concatLatestFrom(() =>
        selectorOrSelectors.map((selector) => this._store.select(selector)),
      )
    }
    return concatLatestFrom(() =>
      this._store.select(selectorOrSelectors as MemoizedSelector<object, any>),
    )
  }
}
